import { getMountedApps } from 'single-spa'

import * as Sentry from '@sentry/react'

const SHELL_APP_NAMES = new Set(['@cosmica-shell-ui/shell-ui', '@cosmica-shell-ui/shell-homepage', '@cosmica-shell-ui/page-error'])

let remoteOverrideNames: string[] = []

/**
 * Records which remotes are being served from a `?remote.<name>=<url>` dev override,
 * so every event of the session carries an `mfe.override` tag and is never mistaken
 * for a regression of the deployed build.
 */
export function tagRemoteOverrides(names: string[]): void {
  remoteOverrideNames = [...names]
}

export type MfeFailurePhase = 'meta' | 'load' | 'lifecycle' | 'registry' | 'config' | 'bootstrap'

/**
 * A failure of the same phase and scope is reported again only after this window:
 * one incident becomes one event, a genuinely new failure minutes later is not lost.
 */
export const MFE_FAILURE_DEDUPE_WINDOW_MS = 10 * 60_000

const reportedFailures = new Map<string, number>()

function toError(error: unknown): Error {
  if (error instanceof Error) return error
  if (error && typeof error === 'object' && 'message' in error) return new Error(String((error as { message: unknown }).message))
  return new Error(String(error))
}

/**
 * Reports a micro-frontend failure to Sentry once per dedupe window.
 *
 * Dedupe key: a remote whose remoteEntry is unreachable fails once for its meta or once
 * per route when its chunks disappear later; both are one incident, so `meta` and `load`
 * failures are keyed and fingerprinted per remote. `lifecycle`, `registry` and `config`
 * failures are specific to one exposed module and are keyed per module. `bootstrap` is a
 * shell-level failure reported under the shell's own app names.
 */
export function captureMfeFailure(fullExposedName: string, phase: MfeFailurePhase, error: unknown): void {
  const remote = fullExposedName.split('/')[0]
  const scope = phase === 'meta' || phase === 'load' ? remote : fullExposedName
  const key = `${phase}:${scope}`
  const now = Date.now()
  const lastReport = reportedFailures.get(key)
  if (lastReport !== undefined && now - lastReport < MFE_FAILURE_DEDUPE_WINDOW_MS) return
  reportedFailures.set(key, now)

  Sentry.captureException(toError(error), {
    tags: { mfe: fullExposedName, 'mfe.remote': remote, 'mfe.phase': phase },
    fingerprint: ['mfe-failure', phase, scope]
  })
}

/**
 * Sentry must be initialized before Module Federation loads any remote,
 * so that errors thrown by the MFEs are captured too (shell and remotes
 * share the same page and the same SDK instance).
 *
 * The SDK only runs on deployed builds (Vite "production" mode). The local
 * dev server runs in "development" mode and is skipped, so developer noise
 * never reaches Sentry and the project quota is preserved. Deployed builds
 * carry placeholders (__SENTRY_DSN__ / __SENTRY_ENV__) that nginx replaces
 * at container start from the per-environment Helm config; if they are not
 * substituted the DSN guard below keeps the SDK a no-op.
 */
export function initSentry(): void {
  if (!import.meta.env.PROD) {
    console.debug('[SHELL] Sentry disabled: development mode')
    return
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN

  // Empty, or an unsubstituted "__SENTRY_DSN__" placeholder: either way an
  // invalid DSN would leave the SDK half-initialized, so bail out instead.
  if (!dsn || dsn.startsWith('__')) {
    console.debug('[SHELL] Sentry disabled: VITE_SENTRY_DSN not configured')
    return
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    // Error tracking only for now: no performance tracing.
    tracesSampleRate: 0,
    sendDefaultPii: false,
    // Known noise. Never list MFE load errors here: captureMfeFailure reports
    // them with context and ignoreErrors would drop those events as well.
    ignoreErrors: ['ResizeObserver loop completed with undelivered notifications', 'ResizeObserver loop limit exceeded'],
    denyUrls: [/^chrome:\/\//i, /^chrome-extension:\/\//i, /^moz-extension:\/\//i, /^safari(-web)?-extension:\/\//i],
    integrations: [
      Sentry.feedbackIntegration({
        // No floating button: the widget is opened from the topbar icon
        // rendered by shell-ui (see AuthenticatedApp), so it does not
        // overlap MFE content or the chatbot drawer.
        autoInject: false,
        showBranding: false
      })
    ],
    // The browser SDK derives OS/browser from the user agent but never
    // captures screen geometry, so we attach it ourselves. Read at send time
    // (not once at init) so it reflects the state when the error happened —
    // e.g. after the window moved to a differently-scaled monitor.
    beforeSend(event) {
      const { screen, devicePixelRatio, innerWidth, innerHeight } = window

      event.contexts = {
        ...event.contexts,
        // Populates Sentry's native "Device" panel. These are CSS ("logical")
        // pixels: already divided by the OS scale factor (e.g. Windows 125%,
        // where a 1920x1080 panel reports 1536x864). screen_density is
        // devicePixelRatio, so screen_* x density recovers the physical
        // resolution — but only at 100% page zoom: devicePixelRatio also folds
        // in browser zoom, while screen.width/height do not track it.
        device: {
          ...event.contexts?.device,
          screen_resolution: `${screen.width}x${screen.height}`,
          screen_width_pixels: screen.width,
          screen_height_pixels: screen.height,
          screen_density: devicePixelRatio
        }
      }

      // Viewport is usually the more actionable value for layout bugs (it also
      // folds in browser zoom); expose it as a searchable/filterable tag.
      event.tags = { ...event.tags, viewport: `${innerWidth}x${innerHeight}` }

      // Which product was on screen when the event happened: the single-spa
      // apps mounted at send time, minus the shell's own. Read at send time
      // for the same reason as the geometry above.
      const mountedMfes = getMountedApps()
        .filter((name) => !SHELL_APP_NAMES.has(name))
        .map((name) => name.replace(/^@/, ''))
      if (mountedMfes.length > 0) {
        event.tags['mfe.active'] = mountedMfes.join(',')
      }
      if (remoteOverrideNames.length > 0) {
        event.tags['mfe.override'] = remoteOverrideNames.join(',')
      }

      return event
    }
  })
}
